<template>
  <div v-if="editor">
    <div class="texteditor__menubar">
      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('bold') }"
        @click="editor.chain().toggleBold().focus().run()"
      >
        <lucide-icon name="bold" />
      </button>
      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('italic') }"
        @click="editor.chain().toggleItalic().focus().run()"
      >
        <lucide-icon name="italic" />
      </button>
      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('underline') }"
        @click="editor.chain().toggleUnderline().focus().run()"
      >
        <lucide-icon name="underline" />
      </button>

      <div class="texteditor__button p-0">
        <input-color-text class="texteditor__button"
          :value="editor.getAttributes('textStyle').color || null"
          :default-value="'var(--color-text)'"
          popout-class="texteditor__tooltip p-3 pt-2"
          @update:value="setColor"
        />
      </div>

      <tooltip-button
        class="texteditor__button"
        :title="$t('labels.fontSize')"
        placement="top"
      >
        <lucide-icon name="a-arrow-up-down" />
        <template #tooltip>
          <div class="line p-1 gap-1">
            <button
              class="net-actionbar__button"
              type="button"
              @click="increaseFontSize()"
            >
              <lucide-icon name="a-arrow-up" />
            </button>
            <button
              class="net-actionbar__button"
              type="button"
              @click="resetFontSize()"
            >
              <lucide-icon name="eraser" />
            </button>
            <button
              class="net-actionbar__button"
              type="button"
              @click="decreaseFontSize()"
            >
              <lucide-icon name="a-arrow-down" />
            </button>
          </div>
        </template>
      </tooltip-button>

      <tooltip-button
        class="texteditor__button"
        :title="$t('labels.textAlignment')"
        placement="top"
      >
        <lucide-icon :name="editor.isActive({ textAlign: 'center'}) ? 'text-align-center' : editor.isActive({ textAlign: 'end'}) ? 'text-align-end' : 'text-align-start'" />
        <template #tooltip>
          <div class="line gap-1">
            <button
              class="texteditor__button"
              type="button"
              :class="{ 'is-active': editor.isActive({ textAlign: 'start' }) }"
              @click="editor.chain().setTextAlign('start').focus().run()"
            >
              <lucide-icon name="text-align-start" />
            </button>
            <button
              class="texteditor__button"
              type="button"
              :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }"
              @click="editor.chain().setTextAlign('center').focus().run()"
            >
              <lucide-icon name="text-align-center" />
            </button>
            <button
              class="texteditor__button"
              type="button"
              :class="{ 'is-active': editor.isActive({ textAlign: 'end' }) }"
              @click="editor.chain().setTextAlign('end').focus().run()"
            >
              <lucide-icon name="text-align-end" />
            </button>
          </div>
        </template>
      </tooltip-button>

      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('paragraph') }"
        @click="editor.chain().setParagraph().focus().run()"
      >
        <lucide-icon name="type" />
      </button>
      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >
        <lucide-icon name="h-1" />
      </button>
      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        <lucide-icon name="h-2" />
      </button>
      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        <lucide-icon name="h-3" />
      </button>

      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <lucide-icon name="list" />
      </button>

      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <lucide-icon name="list-ordered" />
      </button>

      <template v-if="'setLineHeight' in editor.commands">
        <tooltip-button
          class="texteditor__button"
          :title="$t('labels.lineHeight')"
          placement="top"
        >
          <lucide-icon name="list-chevrons-down-up" />
          <template #tooltip>
            <div class="line p-1 gap-1">
              <button
                class="texteditor__button"
                type="button"
                :title="$t('actions.increaseLineHeight')"
                @click="increaseLineHeight()"
              >
                <lucide-icon name="plus" />
              </button>
              <button
                class="texteditor__button"
                type="button"
                :title="$t('actions.resetLineHeight')"
                @click="resetLineHeight()"
              >
                <lucide-icon name="eraser" />
              </button>
              <button
                class="texteditor__button"
                type="button"
                :title="$t('actions.decreaseLineHeight')"
                @click="decreaseLineHeight()"
              >
                <lucide-icon name="minus" />
              </button>
            </div>
          </template>
        </tooltip-button>
      </template>

      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('blockquote') }"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >
        <lucide-icon name="quote" />
      </button>

      <button
        class="texteditor__button"
        type="button"
        :class="{ 'is-active': editor.isActive('link') }"
        @click="addLink(editor.isActive('link'))"
      >
        <lucide-icon name="link" />
      </button>

      <button
        class="texteditor__button"
        type="button"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >
        <lucide-icon name="hr" />
      </button>

      <button
        class="texteditor__button"
        type="button"
        :disabled="!editor.can().undo()"
        @click="editor.chain().focus().undo().run()"
      >
        <lucide-icon name="undo" />
      </button>

      <button
        class="texteditor__button"
        type="button"
        :disabled="!editor.can().redo()"
        @click="editor.chain().focus().redo().run()"
      >
        <lucide-icon name="redo" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { showMessage } from "@core/ui/alert/alert";

import { Editor } from "@tiptap/core";
import { Fragment, Node } from "prosemirror-model";

import LucideIcon from "@core/ui/LucideIcon.vue";
import InputColorText from "./InputColorText.vue";
import TooltipButton from "@core/ui/TooltipButton.vue";


const props = defineProps<{
  editor?: Editor;
}>();

const emit = defineEmits<{
  (event: "link-created", href: string): void;
}>();

const { t } = useI18n();

const selection = computed(() => {
  return props.editor?.state.selection;
});

const selectedText = computed(() => {
  if (selectionEmpty.value) return "";
  if (!selection.value) return "";
  let fragment = selection.value.content().content;
  if (!fragment) return "";
  let text = getFragmentText(fragment);
  return text.slice(1);
});

const selectionEmpty = computed(() => {
  if (!selection.value) return true;
  return selection.value.empty;
});
    
function getFragmentText(fragment: Fragment & { content?: Node[] | readonly Node[] }) {
  let nodes = fragment.content;
  if (!nodes || nodes.length === 0) return "";

  let text: string = nodes.reduce((text, node) => {
    if (node.text) return (text += " " + node.text);
    let subFragment = node.content;
    return (text += getFragmentText(subFragment));
  }, "");
  return text;
}

async function addLink(remove = false) {
  if (!props.editor) return;
  if (remove) {
    props.editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  if (selectionEmpty.value) {
    showMessage(t("messages.markSelectionForLink"));
    return;
  }

  const href = ensureLink(selectedText.value.toLowerCase());

  props.editor
    .chain()
    .focus()
    .setLink({ href: href, target: "_blank" })
    .run();

  emit("link-created", href);
}

function ensureLink(str: string) {
  if (str == null) return "https://";
  if (
    str.startsWith("http:") ||
    str.startsWith("mailto:") ||
    str.startsWith("https:")
  ) {
    return str;
  }
  return "https://" + str;
}

// ==================
// COLOR

function setColor(color: string | null) {
  if (!props.editor) return;
  if (color == null) {
    props.editor.chain().unsetColor().focus().run();
  } else {
    props.editor.chain().setColor(color).focus().run();
  }
}

// ==================
// FONT SIZE

function increaseFontSize() {
  if (!props.editor) return;
  if ("setFontSize" in props.editor.commands) {
    const textStyle = props.editor.getAttributes("textStyle");
    const currentSize = parseFloat(textStyle.fontSize || "1");
    const newSize = currentSize * 1.2;
    props.editor.chain().setFontSize(newSize + "em").focus().run();
    return;
  }
}

function decreaseFontSize() {
  if (!props.editor) return;
  if ("setFontSize" in props.editor.commands) {
    const currentSize = parseFloat(
      props.editor.getAttributes("textStyle").fontSize || "1"
    );
    console.log("currentSize:", currentSize);
    const newSize = currentSize / 1.2;
    props.editor.chain().setFontSize(newSize + "em").focus().run();
    return;
  }
}

function resetFontSize() {
  if (!props.editor) return;
  if ("setFontSize" in props.editor.commands) {
    props.editor.chain().unsetFontSize().focus().run();
    return;
  }
}

// ==================
// LINE HEIGHT

const LINE_HEIGHT_STEP = 0.05;

function increaseLineHeight() {
  if (!props.editor) return;
  if ("setLineHeight" in props.editor.commands) {
    const textStyle = props.editor.getAttributes("textStyle");
    const currentHeight = parseFloat(textStyle.lineHeight || "1.4");
    const newHeight = currentHeight + LINE_HEIGHT_STEP;
    props.editor.chain().setLineHeight(`${newHeight}`).focus().run();
    return;
  }
}

function decreaseLineHeight() {
  if (!props.editor) return;
  if ("setLineHeight" in props.editor.commands) {
    const textStyle = props.editor.getAttributes("textStyle");
    const currentHeight = parseFloat(textStyle.lineHeight || "1.4");
    const newHeight = Math.max(0.1, currentHeight - LINE_HEIGHT_STEP);
    props.editor.chain().setLineHeight(`${newHeight}`).focus().run();
    return;
  }
}

function resetLineHeight() {
  if (!props.editor) return;
  if ("setLineHeight" in props.editor.commands) {
    props.editor.chain().unsetLineHeight().focus().run();
    return;
  }
}
</script>

<style lang="scss">
.texteditor {
  &__menubar {
    position: sticky;
    top: 0;
    background-color: var(--color-background-deep);
    border-bottom: 1px solid var(--color-border-light);
    padding: 1rem 2rem;
    z-index: 1;

    @media screen and (min-width: 481px) {
      padding-inline: 6rem;
    }
  }

  &__button {
    font-size: 16px;
    display: inline-block;
    background-color: var(--color-background-deep);
    border: none;
    border-radius: 0.3em;
    padding: 0.5em;
    margin: 0 0.1em;
    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: 1px solid var(--color-action);
    }
    &.is-active,
    &:hover {
      background-color: var(--color-background-medium);
    }
    &:disabled {
      color: var(--color-text-light);
    }
    &:disabled:active {
      animation: animation-shake 500ms cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    }
  }

  &__close {
    position: absolute;
    top: 1rem;
    right: 2rem;
    z-index: 1;
  }
}
</style>
